/**
 * editor2/hooks/useSquareInvoice.ts
 * Square インボイスの作成・取り消しフック
 * ReservationEditorPanel の handleCreateInvoice / handleVoidInvoice を抽出
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import type { EditorReservation } from '../types';
import { SYS_INVOICE_BASE } from '../../../constants/calendar';

interface UseSquareInvoiceOptions {
  getData: () => EditorReservation;
  onChange?: (updates: Partial<EditorReservation>) => void;
  onSave?: (data: EditorReservation, options?: { keepOpen?: boolean }) => Promise<void>;
  sysApiBase: string;
}

/**
 * Parse invoice_link to extract actual ID and location token
 * Supports sq:0:xxx and sq:1:xxx prefix format
 */
function parseInvoiceLink(rawValue: string): { actualId: string; locationToken: string } {
  let actualId = rawValue;
  let locationToken = 'LK10T0F948TWA'; // default (Naha: 0)

  if (rawValue.startsWith('sq:')) {
    const parts = rawValue.split(':');
    const sqIndex = parts[1];
    // Invoice ID itself may contain colons, so join from index 2 onwards
    actualId = parts.slice(2).join(':');

    if (sqIndex === '1') {
      locationToken = 'L26HXR0HJHTTQ'; // Naha1: 1
    }
  } else if (rawValue.includes('sq:')) {
    // Legacy fallback
    const parts = rawValue.split(':');
    actualId = parts[parts.length - 1];
  }

  return { actualId, locationToken };
}

export default function useSquareInvoice({
  getData,
  onChange,
  onSave,
  sysApiBase,
}: UseSquareInvoiceOptions) {
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [isVoidingInvoice, setIsVoidingInvoice] = useState(false);

  // Keep latest refs for async callbacks
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const getDataRef = useRef(getData);
  useEffect(() => {
    getDataRef.current = getData;
  }, [getData]);

  const createInvoice = useCallback(
    async (isDirty: boolean) => {
      const safe = getData();

      if (isDirty) {
        if (!window.confirm('変更があります。更新して発行しますか？')) return;
        if (onSaveRef.current) {
          await onSaveRef.current(safe, { keepOpen: true });
        }
      } else {
        if (!window.confirm('請求書を発行しますか？')) return;
      }

      setIsCreatingInvoice(true);
      try {
        // Required field validation
        if (!safe.contact_code) {
          window.alert('予約番号(contact_code)がありません。');
          setIsCreatingInvoice(false);
          return;
        }
        if (!safe.start_date || !safe.end_date) {
          window.alert('開始日時または終了日時が設定されていません。');
          setIsCreatingInvoice(false);
          return;
        }

        const payload = {
          id: safe.id,
          contact_code: safe.contact_code,
          customer_name: safe.customer_name,
          customer_email: safe.customer_email,
          customer_tel: safe.customer_tel,
          customer_address: safe.customer_address,
          start_date: safe.start_date,
          end_date: safe.end_date,
          car_type: safe.car_type,
          vehicle_id: safe.vehicle_id,
          status: safe.status,
          bg_color: safe.bg_color,
          text_color: safe.text_color,
          booking_date: safe.booking_date,
          rental_location: safe.rental_location,
          return_location: safe.return_location,
          pu_staff_id: safe.pu_staff_id,
          do_staff_id: safe.do_staff_id,
          rent_staff_id: safe.rent_staff_id,
          return_staff_id: safe.return_staff_id,
          mail_send_flg1: safe.mail_send_flg1,
          mail_send_flg2: safe.mail_send_flg2,
          mail_send_flg3: safe.mail_send_flg3,
          short_memo: safe.short_memo,
          json_data: safe.json_data,
          days: safe.days,
          nights: safe.nights,
          detail: {
            contact_id: safe.contact_id || safe.contact_code,
            contact_media: safe.contact_media,
            contact_plan: safe.contact_plan,
            communication_tools: safe.communication_tools,
            pax: safe.pax,
            pax_children: safe.pax_children,
            child_seat: safe.child_seat,
            booster_seat: safe.booster_seat,
            junior_seat: safe.junior_seat,
            arrival_flight: safe.arrival_flight,
            departure_flight: safe.departure_flight,
            base_price: safe.base_price,
            option: safe.option,
            option_price: safe.option_price,
            noc: (safe.noc_price ?? 0) > 0,
            noc_price: safe.noc_price,
            cdw: (safe.cdw_price ?? 0) > 0,
            cdw_price: safe.cdw_price,
            coupon: safe.coupon,
            point: safe.point,
            sum_price: safe.sum_price,
            days: safe.days,
            nights: safe.nights,
            invoice_link: safe.invoice_link,
            comment: safe.comment,
            memo: safe.memo,
            emergency_name: safe.emergency_name,
            emergency_rel: safe.emergency_rel,
            emergency_tel: safe.emergency_tel,
            license: safe.license,
            payment: safe.payment,
            terms_and_conditions: safe.terms_and_conditions,
            disclaimer: safe.disclaimer,
            agreement: safe.agreement,
          },
        };

        const res = await fetch(`${SYS_INVOICE_BASE}/square_create_invoice.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data: any;
        try {
          data = JSON.parse(text);
        } catch (jsonError) {
          console.error('[useSquareInvoice] JSON parse error:', jsonError);
          if (!res.ok) {
            window.alert(
              `請求書の発行に失敗しました\n\nHTTPステータス: ${res.status} ${res.statusText}\n` +
                `エラー内容:\n${text.slice(0, 300)}${text.length > 300 ? '...' : ''}`,
            );
            setIsCreatingInvoice(false);
            return;
          }
          throw new Error('Invalid JSON response: ' + text.slice(0, 100));
        }

        if (data.status === 'success' && data.invoice_id) {
          onChange?.({ invoice_link: data.invoice_id });
          window.alert('請求書を発行しました');

          // Auto-save after onChange propagates
          if (onSaveRef.current) {
            setTimeout(() => {
              const latest = getDataRef.current();
              onSaveRef.current?.(latest, { keepOpen: true });
            }, 300);
          }
        } else {
          const errorMsg =
            typeof data.message === 'string'
              ? data.message
              : JSON.stringify(data.message || 'Unknown error');
          const detailsMsg = data.details
            ? '\n詳細: ' + JSON.stringify(data.details, null, 2)
            : '';
          console.error('[useSquareInvoice] Invoice creation failed:', data);
          window.alert(`請求書の発行に失敗しました\n\nエラー: ${errorMsg}${detailsMsg}`);
        }
      } catch (e: any) {
        console.error('[useSquareInvoice] Create invoice error:', e);
        window.alert(`エラーが発生しました\n\n${e.message}`);
      } finally {
        setIsCreatingInvoice(false);
      }
    },
    [getData, onChange, sysApiBase],
  );

  const voidInvoice = useCallback(async () => {
    const safe = getData();
    const invoiceId = String(safe.invoice_link ?? '').trim();
    if (!invoiceId) return;

    if (!window.confirm('この請求書をSquare上で取り消しますか？')) return;

    setIsVoidingInvoice(true);
    try {
      const payload = { invoice_id: invoiceId };
      const res = await fetch(`${SYS_INVOICE_BASE}/square_void_invoice.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let data: any;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error('Invalid JSON response: ' + text.slice(0, 100));
      }

      if (data.status === 'success') {
        onChange?.({ invoice_link: '' });
        window.alert('請求書を取り消しました');

        // Auto-save
        if (onSaveRef.current) {
          setTimeout(() => {
            const latest = getDataRef.current();
            onSaveRef.current?.(latest, { keepOpen: true });
          }, 300);
        }
      } else {
        const errorMsg =
          typeof data.message === 'string'
            ? data.message
            : JSON.stringify(data.message || 'Unknown error');
        console.error('[useSquareInvoice] Void invoice failed:', data);
        window.alert(`請求書の取り消しに失敗しました\n\nエラー: ${errorMsg}`);
      }
    } catch (e: any) {
      console.error('[useSquareInvoice] Void invoice error:', e);
      window.alert(`エラーが発生しました\n\n${e.message}`);
    } finally {
      setIsVoidingInvoice(false);
    }
  }, [getData, onChange, sysApiBase]);

  const openInvoiceLink = useCallback(() => {
    const safe = getData();
    const rawValue = String(safe.invoice_link ?? '').trim();
    if (!rawValue) return;

    const { actualId, locationToken } = parseInvoiceLink(rawValue);
    const targetUrl = `https://app.squareup.com/dashboard/invoices/${actualId}/edit?currentUnitToken=${locationToken}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, [getData]);

  return {
    isCreatingInvoice,
    isVoidingInvoice,
    createInvoice,
    voidInvoice,
    openInvoiceLink,
  };
}
